/**
 * Id / token helpers. Uses the Web Crypto API (`globalThis.crypto`), which is available in
 * Node ≥ 18 and all modern browsers — so this module stays isomorphic (safe in the SPAs too).
 */

/**
 * The two things this module needs from Web Crypto, described locally.
 *
 * Not the DOM's `Crypto`: React Native compiles without the DOM lib, so naming
 * that type made this shared module fail to typecheck in the app that imports
 * it most. The runtime object is the same one either way — Expo's runtime
 * provides it, as do Node ≥ 18 and every browser.
 */
interface RandomSource {
  randomUUID(): string;
  getRandomValues<T extends Uint8Array>(array: T): T;
}

function webCrypto(): RandomSource {
  const c = (globalThis as { crypto?: RandomSource }).crypto;
  if (!c) throw new Error('Web Crypto API is not available in this runtime');
  return c;
}

/** Opaque entity id (UUID v4). */
export function newId(): string {
  return webCrypto().randomUUID();
}

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32 (no I/L/O/U)

/** A short, URL- and human-safe random token of `len` chars (default 12). */
export function shortToken(len = 12): string {
  const bytes = new Uint8Array(len);
  webCrypto().getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < len; i++) out += ALPHABET[bytes[i]! % ALPHABET.length];
  return out;
}

/**
 * Human-facing order number, e.g. "TS-20260512-7F3KQ9".
 * Uniqueness is still enforced by a DB unique constraint; the random suffix just avoids collisions.
 */
export function newOrderNumber(prefix = 'TS', now: Date = new Date()): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  const d = String(now.getUTCDate()).padStart(2, '0');
  return `${prefix}-${y}${m}${d}-${shortToken(6)}`;
}

/** Invoice number, e.g. "INV/2026-27/000123" — caller supplies the running sequence. */
export function formatInvoiceNumber(seq: number, financialYear: string, prefix = 'INV'): string {
  return `${prefix}/${financialYear}/${String(seq).padStart(6, '0')}`;
}

/** Indian financial year string for a date, e.g. 2026-05-12 -> "2026-27". */
export function indianFinancialYear(date: Date = new Date()): string {
  const month = date.getUTCMonth() + 1; // 1..12
  const year = date.getUTCFullYear();
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}
